import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  UserCog, Plus, Edit, Trash2, Key, Shield, 
  ShieldCheck, Eye, Loader2, Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminManagement = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    is_active: true
  });
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdmins(data);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleCreate = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error(language === 'pt' ? 'Preencha todos os campos' : 'Fill all fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(language === 'pt' ? 'Admin criado!' : 'Admin created!');
        setShowCreateDialog(false);
        resetForm();
        fetchAdmins();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao criar admin' : 'Error creating admin');
    }
  };

  const handleUpdate = async () => {
    if (!selectedAdmin) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedAdmin.user_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          role: formData.role,
          is_active: formData.is_active
        })
      });

      if (response.ok) {
        toast.success(language === 'pt' ? 'Admin atualizado!' : 'Admin updated!');
        setShowEditDialog(false);
        fetchAdmins();
      } else {
        toast.error(language === 'pt' ? 'Erro ao atualizar' : 'Error updating');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao atualizar' : 'Error updating');
    }
  };

  const handleChangePassword = async () => {
    if (!selectedAdmin) return;
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(language === 'pt' ? 'Senhas não coincidem' : 'Passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error(language === 'pt' ? 'Senha muito curta (min 6 caracteres)' : 'Password too short (min 6 chars)');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedAdmin.user_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: passwordData.new_password })
      });

      if (response.ok) {
        toast.success(language === 'pt' ? 'Senha alterada!' : 'Password changed!');
        setShowPasswordDialog(false);
        setPasswordData({ new_password: '', confirm_password: '' });
      } else {
        toast.error(language === 'pt' ? 'Erro ao alterar senha' : 'Error changing password');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao alterar senha' : 'Error changing password');
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm(language === 'pt' ? 'Tem certeza que deseja excluir?' : 'Are you sure you want to delete?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${adminId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(language === 'pt' ? 'Admin excluído!' : 'Admin deleted!');
        fetchAdmins();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao excluir' : 'Error deleting');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'admin',
      is_active: true
    });
  };

  const openEditDialog = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      role: admin.role,
      is_active: admin.is_active
    });
    setShowEditDialog(true);
  };

  const openPasswordDialog = (admin) => {
    setSelectedAdmin(admin);
    setPasswordData({ new_password: '', confirm_password: '' });
    setShowPasswordDialog(true);
  };

  const getRoleBadge = (role) => {
    const config = {
      super_admin: { color: 'bg-purple-100 text-purple-700', icon: ShieldCheck, label: 'Super Admin' },
      admin: { color: 'bg-blue-100 text-blue-700', icon: Shield, label: 'Admin' },
      moderator: { color: 'bg-gray-100 text-gray-700', icon: Eye, label: language === 'pt' ? 'Moderador' : 'Moderator' }
    };
    const c = config[role] || config.admin;
    return (
      <Badge className={c.color}>
        <c.icon size={12} className="mr-1" />
        {c.label}
      </Badge>
    );
  };

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <UserCog className="text-[#FF6B35]" size={20} />
            {language === 'pt' ? 'Gestão de Administradores' : 'Admin Management'}
          </span>
          {isSuperAdmin && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full text-xs" data-testid="create-admin-btn">
                  <Plus size={14} className="mr-1" />
                  {language === 'pt' ? 'Novo Admin' : 'New Admin'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{language === 'pt' ? 'Criar Novo Admin' : 'Create New Admin'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>{language === 'pt' ? 'Nome' : 'Name'} *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{language === 'pt' ? 'Senha' : 'Password'} *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({...prev, password: e.target.value}))}
                      className="mt-1"
                      placeholder={language === 'pt' ? 'Mínimo 6 caracteres' : 'Minimum 6 characters'}
                    />
                  </div>
                  <div>
                    <Label>{language === 'pt' ? 'Nível de Acesso' : 'Access Level'}</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData(prev => ({...prev, role: v}))}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="super_admin">Super Admin - {language === 'pt' ? 'Acesso Total' : 'Full Access'}</SelectItem>
                        <SelectItem value="admin">Admin - {language === 'pt' ? 'Gestão' : 'Management'}</SelectItem>
                        <SelectItem value="moderator">{language === 'pt' ? 'Moderador - Apenas Visualização' : 'Moderator - View Only'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleCreate} className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
                    <Save size={16} className="mr-2" />
                    {language === 'pt' ? 'Criar Admin' : 'Create Admin'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-[#9FA2B4] text-sm mb-4">
          {language === 'pt' 
            ? 'Níveis: Super Admin (acesso total) > Admin (gestão) > Moderador (visualização)'
            : 'Levels: Super Admin (full access) > Admin (management) > Moderator (view only)'}
        </p>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-[#FF6B35]" />
          </div>
        ) : (
          <div className="space-y-3">
            {admins.map((admin) => (
              <div 
                key={admin.user_id}
                className="flex items-center justify-between p-3 bg-[#F4F1EA] rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#004E64] rounded-full flex items-center justify-center text-white font-bold">
                    {admin.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-[#004E64]">{admin.name}</p>
                    <p className="text-sm text-[#9FA2B4]">{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getRoleBadge(admin.role)}
                  {admin.is_active === false && (
                    <Badge className="bg-red-100 text-red-700">
                      {language === 'pt' ? 'Inativo' : 'Inactive'}
                    </Badge>
                  )}
                  {(isSuperAdmin || admin.user_id === user?.user_id) && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openPasswordDialog(admin)}
                        title={language === 'pt' ? 'Alterar Senha' : 'Change Password'}
                      >
                        <Key size={14} />
                      </Button>
                      {isSuperAdmin && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(admin)}
                          >
                            <Edit size={14} />
                          </Button>
                          {admin.user_id !== user?.user_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(admin.user_id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{language === 'pt' ? 'Editar Admin' : 'Edit Admin'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{language === 'pt' ? 'Nome' : 'Name'}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{language === 'pt' ? 'Nível de Acesso' : 'Access Level'}</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData(prev => ({...prev, role: v}))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="moderator">{language === 'pt' ? 'Moderador' : 'Moderator'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label>{language === 'pt' ? 'Ativo' : 'Active'}</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(c) => setFormData(prev => ({...prev, is_active: c}))}
                />
              </div>
              <Button onClick={handleUpdate} className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
                <Save size={16} className="mr-2" />
                {language === 'pt' ? 'Guardar' : 'Save'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{language === 'pt' ? 'Alterar Senha' : 'Change Password'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#9FA2B4]">
                {language === 'pt' ? 'Alterando senha para:' : 'Changing password for:'} <strong>{selectedAdmin?.name}</strong>
              </p>
              <div>
                <Label>{language === 'pt' ? 'Nova Senha' : 'New Password'}</Label>
                <Input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  className="mt-1"
                  placeholder={language === 'pt' ? 'Mínimo 6 caracteres' : 'Minimum 6 characters'}
                />
              </div>
              <div>
                <Label>{language === 'pt' ? 'Confirmar Senha' : 'Confirm Password'}</Label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  className="mt-1"
                />
              </div>
              <Button onClick={handleChangePassword} className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
                <Key size={16} className="mr-2" />
                {language === 'pt' ? 'Alterar Senha' : 'Change Password'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AdminManagement;
